/**
 * La entrevista, conversando (GKTIBO-88).
 *
 * Reemplaza al formulario de una pregunta por vez. **No conviven**: dos
 * caminos al mismo archivo ya costaron un bug (GKTIBO-86).
 *
 * ## La tarjeta de confirmación ES el editor
 *
 * El dueño nunca toca el `.md` —no es técnico, y el conocimiento lo construye
 * siempre un asistente (decisión del PO, 2026-09-19)—. Lo que sí hace es ver
 * el texto exacto antes de que se guarde y aprobarlo. Esa persona entre el
 * modelo y el archivo es lo único que impide que una invención llegue al bot
 * en silencio.
 *
 * Y «Corregir» no abre una caja de texto: le habla a Tibo.
 *
 * ## Dónde está el progreso
 *
 * En el panel de la izquierda, no acá. Un formulario decía «3 de 6»; una
 * conversación no dice nada, y no saber cuánto falta es el momento exacto en
 * que alguien abandona.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.ts';
import type { Voz } from './ComoSuena';
import { RedactorDeLaEntrevista } from './RedactorDeLaEntrevista';
import { type Turno, TurnoDeLaEntrevista } from './TurnoDeLaEntrevista';
import { entrevista } from './textos-entrevista';

interface ConversacionProps {
  tenantId: string;
  /** El nombre del negocio. De ahí sale la inicial del dueño en el hilo. */
  nombre: string;
  /** Tras guardar hay que volver a preguntarle al servidor qué sabe el bot. */
  onGuardado: () => void;
}

export function Conversacion({ tenantId, nombre, onGuardado }: ConversacionProps) {
  const t = entrevista.chat;
  // La inicial del negocio, como en el diseño. Decía «Vos» —una palabra
  // apretada en un recuadro de 28px, que se leía como una etiqueta mal puesta.
  const inicial = nombre.trim().charAt(0).toUpperCase() || 'V';
  const [turnos, setTurnos] = useState<Turno[]>([{ id: 0, de: 'tibo', texto: t.saludo }]);
  /** La primera pregunta se hace una sola vez, aunque el query revalide. */
  const yaPregunto = useRef(false);
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const siguienteId = useRef(1);
  /**
   * La pregunta de la cola que Tibo está haciendo (GKTIBO-148). Cuando el
   * dueño guarda su respuesta, se marca contestada y se pide la siguiente.
   */
  const preguntaEnCurso = useRef<string | null>(null);
  const finDelHilo = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // La llamada va INLINE, sin pasar por `alFinal`: referenciarlo obligaría a
    // declararlo como dependencia, y como se recrea en cada render el efecto
    // correría siempre en vez de una sola vez al montar.
    finDelHilo.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, []);

  /**
   * Con qué arranca: **Tibo pregunta primero**.
   *
   * Antes decía «te voy a hacer unas preguntas cortas» y se quedaba callado.
   * El dueño tenía que escribir algo para que pasara algo, y lo primero que
   * escribiera se guardaba como respuesta: el 2026-09-21 un «ok preguntame»
   * terminó en «Qué NO hacés».
   */
  const apertura = useQuery({
    queryKey: ['apertura-conversacion', tenantId],
    queryFn: () => api.tenants.aperturaDeConversacion(tenantId),
  });

  // Estable entre renders: `setTurnos` con función no necesita leer `turnos`.
  // Eso es lo que permite que `preguntarPor` tampoco cambie, y que el efecto
  // de apertura pueda declararla como dependencia sin recrearse siempre.
  const agregar = useCallback((turno: Omit<Turno, 'id'>) => {
    setTurnos((previos) => [...previos, { ...turno, id: siguienteId.current++ }]);
    requestAnimationFrame(() =>
      finDelHilo.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' }),
    );
  }, []);

  /**
   * La pregunta de un tema, o el cierre si ya no queda ninguno.
   *
   * `useCallback` porque el efecto de apertura la declara como dependencia:
   * una función recreada en cada render ahí adentro es un efecto que corre
   * siempre. Va DESPUÉS de `agregar` porque el linter analiza en orden.
   */
  const preguntarPor = useCallback(
    (tema: string | null, pregunta?: { id: string; texto: string } | null) => {
      // La voz no es una pregunta abierta: es una tarjeta con tres
      // decisiones. Si entrara por el camino de texto, lo que el dueño
      // escribiera terminaría en el tema «voz», que la ruta rechaza.
      if (tema === 'tono') {
        agregar({ de: 'tibo', texto: entrevista.voz.pregunta, esLaVoz: true });
        return;
      }
      // Terminada la agenda, lo que el sistema juntó para preguntar: de a una.
      if (!tema && pregunta) {
        preguntaEnCurso.current = pregunta.id;
        agregar({ de: 'tibo', texto: pregunta.texto });
        return;
      }
      preguntaEnCurso.current = null;
      const copy = tema
        ? entrevista.preguntas[tema as keyof typeof entrevista.preguntas]
        : undefined;
      agregar({ de: 'tibo', texto: copy ? copy.q : t.terminado });
    },
    [agregar, t.terminado],
  );

  /**
   * Cuál sigue lo decide el SERVIDOR. Y un fallo acá NO es «terminaste»:
   * mapearlo a `null` hacía que Tibo dijera «ya sabe lo que necesita» con
   * temas en falta — una frase de éxito en el camino del fallo (L43).
   */
  const seguirPreguntando = useCallback(async () => {
    try {
      const siguiente = await api.tenants.aperturaDeConversacion(tenantId);
      preguntarPor(siguiente.temaEnCurso, siguiente.pregunta);
    } catch {
      setError(t.noSePudoSaberCualSigue);
    }
  }, [tenantId, preguntarPor, t.noSePudoSaberCualSigue]);

  useEffect(() => {
    if (apertura.data === undefined || yaPregunto.current) return;
    yaPregunto.current = true;
    preguntarPor(apertura.data.temaEnCurso, apertura.data.pregunta);
  }, [apertura.data, preguntarPor]);

  const conversar = useMutation({
    mutationFn: (mensaje: string) => api.tenants.conversar(tenantId, mensaje),
    onSuccess: (res) => {
      setError(null);
      if (res.propuestas.length > 0) {
        agregar({
          de: 'tibo',
          texto: t.voyAGuardar,
          propuestas: res.propuestas,
          ...(res.sinAsistente && { sinAsistente: true }),
        });
      } else if (res.repregunta) {
        // El modelo entendió y no había nada que guardar —el dueño saludó, o
        // preguntó algo—. Se sigue conversando, no se guarda ruido.
        agregar({ de: 'tibo', texto: res.repregunta });
      } else {
        agregar({ de: 'tibo', texto: t.terminado });
      }
    },
    onError: () => setError(t.noSePudoConversar),
  });

  /**
   * Guardar: acá y sólo acá se escribe el conocimiento del bot.
   *
   * Un tema por llamada. Si una falla, las otras ya guardaron — que es mejor
   * que perderlas todas por una.
   */
  const guardar = useMutation({
    mutationFn: async (turno: Turno) => {
      for (const p of turno.propuestas ?? []) {
        await api.tenants.cerrarTema(tenantId, p.tema, p.texto);
      }
    },
    onSuccess: async (_r, turno) => {
      setError(null);
      setTurnos((previos) =>
        previos.map((x) => (x.id === turno.id ? { ...x, resuelto: 'guardado' } : x)),
      );
      onGuardado();

      // Si lo que se contestó era una pregunta de la cola, sale de la cola.
      // Si no se pudo marcar, se vuelve a preguntar: molesto, no grave.
      const pregunta = preguntaEnCurso.current;
      if (pregunta) {
        preguntaEnCurso.current = null;
        await api.tenants.resolverPregunta(tenantId, pregunta).catch(() => undefined);
      }

      // Y se sigue preguntando: antes Tibo guardaba y se quedaba callado, y el
      // dueño tenía que adivinar que la conversación seguía (2026-09-21).
      await seguirPreguntando();
    },
    // Su propio mensaje: lo que falló fue guardar, no entender. El del chat
    // hacía creer que el problema era la respuesta que acababa de escribir.
    onError: () => setError(t.noSePudoGuardar),
  });

  /**
   * Guardar la voz: ruta propia, porque llega estructurada.
   *
   * Y después **se sigue preguntando**, igual que con los temas: cuál sigue lo
   * decide el servidor. Recalcularlo acá sería un segundo cálculo que tarde o
   * temprano dice algo distinto del primero.
   */
  const guardarVoz = useMutation({
    mutationFn: ({ voz }: { voz: Voz; turno: Turno }) => api.tenants.guardarLaVoz(tenantId, voz),
    onSuccess: async (_r, { turno }) => {
      setError(null);
      setTurnos((previos) =>
        previos.map((x) => (x.id === turno.id ? { ...x, resuelto: 'guardado' } : x)),
      );
      onGuardado();
      await seguirPreguntando();
    },
    onError: () => setError(t.noSePudoGuardar),
  });

  /**
   * ¿Hay una tarjeta de voz esperando respuesta?
   *
   * Mientras la haya, el campo de texto se apaga. Si no, lo que el dueño
   * escribiera se rutearía al tema «voz» —que la ruta rechaza con 400— y él
   * vería un error sin entender qué hizo mal.
   */
  const esperandoLaVoz = turnos.some((x) => x.esLaVoz && !x.resuelto);

  function corregir(turno: Turno) {
    setTurnos((previos) =>
      previos.map((x) => (x.id === turno.id ? { ...x, resuelto: 'corrigiendo' } : x)),
    );
    agregar({ de: 'tibo', texto: t.comoQuerésQueQuede });
  }

  function enviar() {
    const mensaje = texto.trim();
    if (!mensaje || conversar.isPending) return;
    agregar({ de: 'dueno', texto: mensaje });
    setTexto('');
    conversar.mutate(mensaje);
  }

  return (
    <section className="alta-chat" data-testid="alta-conversacion">
      <div className="alta-chat-hilo" role="log" aria-live="polite" aria-label={entrevista.titulo}>
        {turnos.map((turno) => (
          <TurnoDeLaEntrevista
            key={turno.id}
            turno={turno}
            inicial={inicial}
            tenantId={tenantId}
            nombre={nombre}
            guardandoVoz={guardarVoz.isPending}
            guardando={guardar.isPending}
            onGuardarVoz={(voz) => guardarVoz.mutate({ voz, turno })}
            onGuardar={() => guardar.mutate(turno)}
            onCorregir={() => corregir(turno)}
          />
        ))}

        {conversar.isPending && (
          <p className="alta-chat-pensando" data-testid="alta-chat-pensando">
            {t.pensando}
          </p>
        )}

        <div ref={finDelHilo} />
      </div>

      {error && (
        <p className="alta-chat-error" role="alert" data-testid="alta-chat-error">
          {error}
        </p>
      )}

      <RedactorDeLaEntrevista
        texto={texto}
        onTexto={setTexto}
        onEnviar={enviar}
        pensando={conversar.isPending}
        apagado={esperandoLaVoz}
      />
    </section>
  );
}
